import type { ChangeEvent, ComponentPropsWithoutRef, FocusEvent } from "react";
import { forwardRef, useEffect, useId, useState } from "react";
import type {
  DSTextInputAdornmentProps,
  DSTextInputClassNames,
  DSTextInputControlProps,
  DSTextInputDescriptionProps,
  DSTextInputErrorProps,
  DSTextInputFieldProps,
  DSTextInputIcons,
  DSTextInputLabelProps,
  DSTextInputProps,
  DSTextInputResolvedClassNames,
  DSTextInputRootProps,
} from "./types";
import { cn } from "../../utils/cn";

type InputValue = ComponentPropsWithoutRef<"input">["value"];
type InputDefaultValue = ComponentPropsWithoutRef<"input">["defaultValue"];

function hasInputValue(value: InputValue) {
  return String(value).length > 0;
}

function normalizeInputValue(value: InputValue | InputDefaultValue) {
  if (value === undefined) {
    return "";
  }

  return String(value);
}

function resolveClassNames(props: DSTextInputProps): DSTextInputResolvedClassNames {
  return {
    description: props.classNames?.description,
    error: props.classNames?.error,
    field: props.classNames?.field,
    input: props.classNames?.input,
    label: props.classNames?.label,
  };
}

function resolveIcons(props: DSTextInputProps): DSTextInputIcons {
  return {
    left: props.icons?.left,
    right: props.icons?.right,
  };
}

function DSTextInputRoot({ children, className }: DSTextInputRootProps) {
  return (
    <div className={cn("flex w-full flex-col gap-3 font-inter", className)} data-slot="text-input-root">
      {children}
    </div>
  );
}

function DSTextInputField({
  children,
  className,
  hasError,
  htmlFor,
  isFocused,
}: DSTextInputFieldProps) {
  const fieldClassName = cn(
    "flex w-full items-center gap-2 rounded-[12px] border px-4 py-2 transition-colors duration-200 ease-out bg-body-light-mode-1",
    hasError
      ? "border-feedback-danger"
      : isFocused
        ? "border-brand-primary-3"
        : "border-transparent",
    htmlFor ? "cursor-text" : undefined,
    className
  );

  if (htmlFor) {
    return (
      <label className={fieldClassName} data-slot="text-input-field" htmlFor={htmlFor}>
        {children}
      </label>
    );
  }

  return <div className={fieldClassName} data-slot="text-input-field">{children}</div>;
}

function DSTextInputAdornment({ children, className, slot }: DSTextInputAdornmentProps) {
  if (!children) {
    return null;
  }

  return (
    <span className={cn("shrink-0 text-gray-5", className)} data-slot={slot}>
      {children}
    </span>
  );
}

function DSTextInputLabel({
  className,
  isFloating,
  label,
  required,
}: DSTextInputLabelProps) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "pointer-events-none text-sm font-inter absolute left-0 text-[14px] font-semibold leading-[normal] tracking-[0px] text-gray-5 transition-[top,transform,color] duration-200 ease-out",
        isFloating ? "top-0 translate-y-0" : "top-1/2 -translate-y-1/2",
        className
      )}
      data-slot="text-input-label"
    >
      {label}
      {required ? (
        <span className="text-gray-5" data-slot="text-input-required">
          *
        </span>
      ) : null}
    </span>
  );
}

const DSTextInputControl = forwardRef<HTMLInputElement, DSTextInputControlProps>(
  function DSTextInputControl({ className, hasError, isFloating, ...props }, ref) {
    return (
      <input
        ref={ref}
        aria-invalid={hasError}
        className={cn(
          "w-full font-inter border-none bg-transparent p-0 text-[14px] font-normal leading-[normal] tracking-[0px] text-dark-3 outline-none transition-[opacity,transform,margin] duration-200 ease-out placeholder:text-gray-5 disabled:cursor-not-allowed disabled:text-gray-5",
          isFloating
            ? "relative mt-5 h-5 translate-y-0 opacity-100"
            : "absolute inset-0 h-full cursor-text opacity-0",
          className
        )}
        data-slot="text-input-input"
        {...props}
      />
    );
  }
);

function DSTextInputDescription({ children, className }: DSTextInputDescriptionProps) {
  if (!children) {
    return null;
  }

  return (
    <p
      className={cn("text-[12px] font-normal leading-[normal] tracking-[0px] text-gray-5", className)}
      data-slot="text-input-description"
    >
      {children}
    </p>
  );
}

function DSTextInputError({ children, className }: DSTextInputErrorProps) {
  if (!children) {
    return null;
  }

  return (
    <p
      className={cn(
        "text-left text-[12px] font-normal leading-[normal] tracking-[0px] text-feedback-danger",
        className
      )}
      data-slot="text-input-error"
    >
      {children}
    </p>
  );
}

function DSTextInputBase(props: DSTextInputProps) {
  const {
    className,
    classNames: _classNames,
    defaultValue,
    description,
    errorMessage,
    icons: _icons,
    id,
    label,
    onBlur,
    onChange,
    onFocus,
    ref: inputRef,
    required = false,
    value,
    ...restInputProps
  } = props;
  const classes = resolveClassNames(props);
  const resolvedIcons = resolveIcons(props);
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const [isFocused, setIsFocused] = useState(false);
  const [uncontrolledValue, setUncontrolledValue] = useState(() => normalizeInputValue(defaultValue));
  const isControlled = value !== undefined;
  const controlledValue = isControlled ? normalizeInputValue(value) : undefined;
  const inputValue = controlledValue ?? uncontrolledValue;
  const hasValue = isControlled ? hasInputValue(value) : inputValue.length > 0;
  const isFloating = isFocused || hasValue;
  const hasError = Boolean(errorMessage);

  useEffect(() => {
    if (controlledValue !== undefined) {
      setUncontrolledValue(controlledValue);
    }
  }, [controlledValue]);

  function handleFocus(event: FocusEvent<HTMLInputElement>) {
    setIsFocused(true);
    onFocus?.(event);
  }

  function handleBlur(event: FocusEvent<HTMLInputElement>) {
    setIsFocused(false);
    onBlur?.(event);
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    if (!isControlled) {
      setUncontrolledValue(event.currentTarget.value);
    }

    onChange?.(event);
  }

  return (
    <DSTextInputRoot className={className}>
      <DSTextInputField
        className={classes.field}
        hasError={hasError}
        htmlFor={inputId}
        isFocused={isFocused}
      >
        <DSTextInputAdornment slot="text-input-left-icon">
          {resolvedIcons.left}
        </DSTextInputAdornment>

        <div className="relative flex min-h-10 min-w-0 flex-1 flex-col justify-end" data-slot="text-input-content">
          <DSTextInputLabel
            className={classes.label}
            isFloating={isFloating}
            label={label}
            required={required}
          />

          <DSTextInputControl
            ref={inputRef}
            className={classes.input}
            hasError={hasError}
            id={inputId}
            isFloating={isFloating}
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={handleFocus}
            required={required}
            value={inputValue}
            {...restInputProps}
          />
        </div>

        <DSTextInputAdornment slot="text-input-right-icon">
          {resolvedIcons.right}
        </DSTextInputAdornment>
      </DSTextInputField>

      <DSTextInputDescription className={classes.description}>{description}</DSTextInputDescription>
      <DSTextInputError className={classes.error}>{errorMessage}</DSTextInputError>
    </DSTextInputRoot>
  );
}

const DSTextInput = Object.assign(DSTextInputBase, {
  Adornment: DSTextInputAdornment,
  Description: DSTextInputDescription,
  Error: DSTextInputError,
  Field: DSTextInputField,
  Label: DSTextInputLabel,
  Root: DSTextInputRoot,
});

export { DSTextInput };
export type {
  DSTextInputAdornmentProps,
  DSTextInputClassNames,
  DSTextInputControlProps,
  DSTextInputDescriptionProps,
  DSTextInputErrorProps,
  DSTextInputFieldProps,
  DSTextInputIcons,
  DSTextInputLabelProps,
  DSTextInputProps,
  DSTextInputResolvedClassNames,
  DSTextInputRootProps,
};
